import { ContactBlock } from "../../util/Batch";

/** Saved contact block as the backend returns it. */
export interface SavedAddress extends ContactBlock {
	id: number;
	client?: number;
	is_default?: boolean;
	is_active?: boolean;
}

const FIELDS = ["label", "con_name", "con_phone", "con_email", "con_address", "con_details"] as const;

/** Lines printed under the name, in the order the documents use. */
const LINE_FIELDS = ["label", "con_phone", "con_email", "con_address", "con_details"] as const;

const clean = (value: unknown): string => String(value ?? "").trim();

export const emptyContact = (): ContactBlock => ({
	address_id: null,
	label: "",
	con_name: "",
	con_phone: "",
	con_email: "",
	con_address: "",
	con_details: "",
});

/** Trimmed block, or null when nothing was filled in. */
export const normalizeContact = (block?: ContactBlock | null): ContactBlock | null => {
	if (!block) return null;
	const next: ContactBlock = { address_id: block.address_id ?? null };
	let filled = false;
	for (const field of FIELDS) {
		const value = clean(block[field]);
		if (value) {
			next[field] = value;
			filled = true;
		}
	}
	return filled ? next : null;
};

export const hasContact = (block?: ContactBlock | null): boolean => normalizeContact(block) !== null;

/** What shows on the button next to the client picker. */
export const contactSummary = (block?: ContactBlock | null): string => {
	const normalized = normalizeContact(block);
	if (!normalized) return "";
	const parts = [normalized.label, normalized.con_name].map(clean).filter(Boolean);
	if (parts.length) return parts.join(" — ");
	for (const field of LINE_FIELDS) {
		const value = clean(normalized[field]);
		if (value) return value;
	}
	return "";
};

/** The block as it goes out with a sale or an offer. */
export const contactToPayload = (
	block?: ContactBlock | null,
	options?: { saveToBook?: boolean; makeDefault?: boolean },
): (ContactBlock & { save_to_book: boolean; make_default: boolean }) | null => {
	const normalized = normalizeContact(block);
	if (!normalized) return null;
	return {
		...normalized,
		save_to_book: options?.saveToBook ?? block?.save_to_book ?? true,
		make_default: options?.makeDefault ?? block?.make_default ?? false,
	};
};

/** Read a block back out of a fetched sale / offer payload. */
export const contactFromDetails = (details?: Record<string, unknown> | null): ContactBlock | null => {
	if (!details) return null;
	const block: ContactBlock = { address_id: (details.contact_address_id as number) ?? null };
	for (const field of FIELDS) {
		const value = clean(details[field]);
		if (value) block[field] = value;
	}
	return normalizeContact(block);
};

/**
 * Which saved entry should fill an empty form: only when the client has exactly
 * one. With several the user picks; with none there is nothing to fill.
 * Never overwrites what is already typed.
 */
export const autoFillFrom = (
	saved: readonly SavedAddress[],
	current?: ContactBlock | null,
): SavedAddress | null => {
	if (normalizeContact(current)) return null;
	return saved.length === 1 ? saved[0] : null;
};

/** Same block, no longer tied to a saved entry (used when the client changes). */
export const detachFromBook = (block?: ContactBlock | null): ContactBlock | null => {
	const normalized = normalizeContact(block);
	if (!normalized) return null;
	return { ...normalized, address_id: null };
};

/** A saved entry as it fills the dialog. */
export const contactFromSaved = (saved: SavedAddress): ContactBlock => {
	const block: ContactBlock = { address_id: saved.id };
	for (const field of FIELDS) {
		const value = clean(saved[field]);
		if (value) block[field] = value;
	}
	return block;
};

export const savedAddressLabel = (saved: SavedAddress): string =>
	contactSummary(contactFromSaved(saved)) || `#${saved.id}`;
